import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import {
	ColorPicker,
	isReadable,
	useAutoTransitioningColors,
	useBuiltInColorMap,
} from '@atlassian/color-picker';
import {
	CommonLayout,
	ImageField,
	UnsplashSearchField,
	type UnsplashSearchFieldProps,
	useProductDependencies,
} from '@atlassian/company-hub-common';

import { carouselConfigAnalyticSource } from '../../../../../constants';
import { frameNewParametersV2 } from '../../../../../controllers/use-carousel-extension-parameters-and-actions';
import { type UpdateFrameFormFieldType } from '../../../../../controllers/use-carousel-extension-parameters-and-actions/types';
import { FRAME_FIELDS } from '../../../../../types';
import { CarouselLayoutOptions } from '../../../fields/layout-options';

import { FrameField, type FrameFieldProps } from './frame-field';
import { messages } from './messages';

export type FrameContentProps = {
	showBgAndTextColorOptions?: boolean;
	showLayout?: boolean;
	showMediaPicker?: boolean;
	updateFrameFormField: UpdateFrameFormFieldType;
} & Pick<FrameFieldProps, 'frameParameters' | 'isDisabled'> &
	Pick<UnsplashSearchFieldProps, 'contentId' | 'initialUnsplashResults'>;

const wrapperStyles = xcss({
	alignSelf: 'stretch',
	flexDirection: 'column',
	marginTop: 'space.100',
	marginBottom: 'space.100',
	padding: 'space.0',
});

const bgAndTextColorOptionsWrapperStyles = xcss({
	marginTop: 'space.200',
});

export const FrameContent = ({
	contentId,
	frameParameters,
	initialUnsplashResults,
	isDisabled,
	showBgAndTextColorOptions,
	updateFrameFormField,
	showMediaPicker,
	showLayout,
}: FrameContentProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const {
		cardId,
		imageSrc,
		imageAltText,
		confluencePageId,
		backgroundColor = frameNewParametersV2.backgroundColor,
		textColor = frameNewParametersV2.textColor,
		layout,
	} = frameParameters;
	const { isCompanyHub, enableLinkInputInMediaPicker, isSSRProductInDarkMode, branding } =
		useProductDependencies();
	const { hideDefaultColorPalette, brandBackgroundColorMap } = branding ?? {};
	const { backgroundColorMap, textColorMap, emptyColorMap } =
		useBuiltInColorMap(isSSRProductInDarkMode);
	const isReadableBackgroundColor = (color: string) => isReadable(color, textColor);
	const isReadableTextColor = (color: string) => isReadable(color, backgroundColor);

	const updateBackgroundColor = (bgColor: string) => {
		updateFrameFormField(cardId, {
			[FRAME_FIELDS.BACKGROUND_COLOR]: bgColor,
		});
	};
	const updateTextColor = (color: string) => {
		updateFrameFormField(cardId, {
			[FRAME_FIELDS.TEXT_COLOR]: color,
		});
	};

	const canShowImagePicker = layout !== CommonLayout.NO_IMAGE_BACKGROUND;

	useAutoTransitioningColors(
		backgroundColor,
		isReadableTextColor,
		textColor,
		updateTextColor,
		branding?.defaultParameters,
	);

	return (
		<Flex xcss={wrapperStyles}>
			{showLayout && (
				<CarouselLayoutOptions
					frameParameters={frameParameters}
					updateFrameFormField={updateFrameFormField}
					contentId={contentId}
				/>
			)}
			<FrameField
				frameParameters={frameParameters}
				isDisabled={isDisabled}
				updateFrameFormField={updateFrameFormField}
			/>
			{canShowImagePicker &&
				(showMediaPicker ? (
					<ImageField
						shouldAllowLinkFromUrl={enableLinkInputInMediaPicker}
						analyticsSource={carouselConfigAnalyticSource}
						itemId={cardId}
						contentId={contentId}
						imageSrc={imageSrc}
						imageAltText={imageAltText}
						updateItemFormField={updateFrameFormField}
					/>
				) : (
					<UnsplashSearchField
						analyticsSource={carouselConfigAnalyticSource}
						idPrefix="frame"
						itemId={cardId}
						contentId={contentId}
						confluencePageId={confluencePageId}
						imageSrc={imageSrc}
						imageAltText={imageAltText}
						initialUnsplashResults={initialUnsplashResults}
						isNoImageAllowed={false}
						updateItemFormField={updateFrameFormField}
					/>
				))}
			{showBgAndTextColorOptions && (
				<Stack space="space.200" xcss={bgAndTextColorOptionsWrapperStyles}>
					<Inline alignBlock="center" spread="space-between">
						<Text color="color.text">
							<FormattedMessage {...messages.backgroundColor} />
						</Text>
						<ColorPicker
							builtInColorMap={
								hideDefaultColorPalette && brandBackgroundColorMap
									? brandBackgroundColorMap
									: backgroundColorMap
							}
							isReadableColor={isReadableBackgroundColor}
							onChange={updateBackgroundColor}
							value={backgroundColor}
							pickerTitle={messages.backgroundColor}
							validationSuccessMessage={formatMessage(messages.colorValidationSuccessMessage)}
							validationFailMessage={formatMessage(messages.colorValidationFailMessage)}
							showCustomPalette={isCompanyHub}
							analyticsSource={carouselConfigAnalyticSource}
						/>
					</Inline>
					<Inline alignBlock="center" spread="space-between">
						<Text color="color.text">
							<FormattedMessage {...messages.textColor} />
						</Text>
						<ColorPicker
							builtInColorMap={hideDefaultColorPalette ? emptyColorMap : textColorMap}
							isReadableColor={isReadableTextColor}
							onChange={updateTextColor}
							value={textColor}
							pickerTitle={messages.textColor}
							validationSuccessMessage={formatMessage(messages.colorValidationSuccessMessage)}
							validationFailMessage={formatMessage(messages.colorValidationFailMessage)}
							showCustomPalette={isCompanyHub}
							analyticsSource={carouselConfigAnalyticSource}
							showTextIcon
						/>
					</Inline>
				</Stack>
			)}
		</Flex>
	);
};
